import { Button } from "@punchup/ui-common/shadcn/ui/button";
import { ModalOrDrawer } from "@punchup/ui-common/components/common";
import { GripVertical, X } from "lucide-react";

import { CSS } from "@dnd-kit/utilities";
import CarouselItem from "./CarouselItem";
import { VenueCarouselItem } from "@punchup/data/models";
import { cn } from "@punchup/utils/cn";
import { useSortable } from "@dnd-kit/sortable";
import { useState } from "react";

interface SortableCarouselItemProps {
  item: VenueCarouselItem;
  previewUrls: Map<string, string>;
  isDragDisabled: boolean;
  onDelete?: (item: VenueCarouselItem) => void;
  venuePageId: string;
}

export default function SortableCarouselItem({
  item,
  previewUrls,
  isDragDisabled,
  onDelete,
  venuePageId,
}: SortableCarouselItemProps) {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDisappearing, setIsDisappearing] = useState(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id, disabled: isDragDisabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleDeleteClick = () => {
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    setIsDeleteModalOpen(false);
    setIsDisappearing(true);
    setTimeout(() => onDelete?.(item), 200);
  };

  return (
    <>
      <div
        ref={setNodeRef}
        style={style}
        className={cn(
          "relative",
          isDragging && "z-10 scale-[1.02] shadow-lg",
          isDragDisabled && "cursor-default",
          isDisappearing && "scale-0 opacity-0 transition-all duration-200"
        )}
      >
        <button
          {...attributes}
          {...listeners}
          disabled={isDragDisabled}
          className={cn(
            "absolute top-2 left-2 z-10 p-1 bg-white/80 rounded",
            isDragDisabled
              ? "cursor-default"
              : "cursor-grab active:cursor-grabbing"
          )}
          type="button"
        >
          <GripVertical className="w-4 h-4 text-gray-500" />
        </button>
        {onDelete && (
          <button
            onClick={handleDeleteClick}
            className="absolute top-2 right-2 z-10 p-1 bg-white/80 rounded hover:bg-white/90 cursor-pointer"
            type="button"
          >
            <X className="w-4 h-4 text-gray-500" />
          </button>
        )}
        <CarouselItem
          item={item}
          previewUrls={previewUrls}
          venuePageId={venuePageId}
        />
      </div>
      {/* a modal/drawer to confirm "delete" action */}
      <ModalOrDrawer
        isOpen={isDeleteModalOpen}
        onOpenChange={setIsDeleteModalOpen}
      >
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-y-2">
            <h2 className="text-lg font-semibold">Delete {item.type}</h2>
            <div className="flex flex-col gap-y-1">
              <p className="text-sm text-secondary-blue">
                Are you sure you want to remove this {item.type} from your
                carousel?
              </p>
              <p className="text-sm text-secondary-blue">
                This action cannot be undone.
              </p>
            </div>
          </div>
          <div className="flex gap-3 justify-end">
            <Button
              variant="outline"
              onClick={() => setIsDeleteModalOpen(false)}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleConfirmDelete}>
              Delete
            </Button>
          </div>
        </div>
      </ModalOrDrawer>
    </>
  );
}
